import { jsx as _jsx } from "react/jsx-runtime";
import { Grid, ListItemText } from "@material-ui/core";
import { memo, useEffect, useState } from "react";
import { CheckBoxList } from "@ploy-ui/core";
import { AriaErrorScope, DployAutocomplete } from "@ploy-ui/form-fields";
import { useCallback } from "react";
import { TextLiteral } from "../literals";
import { useVariableData } from "@ploy-lib/calculation";
import { isArray } from "lodash";
import { useIntl } from "react-intl";
const emptyArray = [];
/**
 * Custom field used by DNB to dislay a list of AdditionalCoverages.
 * The additional coverages can be checked (selected) and input field can be edited
 *
 */
const AdditionalCoveragesField = memo((props) => {
    const intl = useIntl();
    const { value: availableAdditionalCoverages } = useVariableData("CAR_INSURANCE_EXTERNAL", "AvailableAdditionalCoverages");
    const { value: descriptions } = useVariableData("CAR_INSURANCE_EXTERNAL", "options_EmbProd_AdditionalCoverages_Description");
    const selectedCoverages = !isArray(props.field.value)
        ? emptyArray
        : props.field.value || emptyArray;
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        if (availableAdditionalCoverages) {
            setItems(mapAvailableAdditionalCoverages(availableAdditionalCoverages, selectedCoverages, intl, descriptions));
        }
    }, [
        setItems,
        availableAdditionalCoverages,
        selectedCoverages,
        intl,
        descriptions
    ]);
    useEffect(() => {
        setLoading(false);
    }, [setLoading, availableAdditionalCoverages]);
    const updateItems = useCallback(newItem => {
        setLoading(true);
        let itemsCopy = [...items];
        let newItems = itemsCopy.map(item => item.id === newItem.id ? newItem : item);
        setItems(newItems);
        props.form.setFieldValue(props.field.name, newItems.reduce((acc, i) => i.checked
            ? [
                ...acc,
                {
                    insuranceAmount: i.fields?.length ?? 0
                        ? parseInt(i.fields?.[0]?.value ?? "") ?? 0
                        : 0,
                    coverageTypeCode: i.id,
                    deductibleCode: availableAdditionalCoverages?.find(a => a.coverageTypeCode === i.id)?.deductibles?.[0]?.deductibleCode ?? "",
                    coverageName: i.primary ?? ""
                }
            ]
            : acc, []));
    }, [
        items,
        setLoading,
        props.field.name,
        props.form,
        availableAdditionalCoverages
    ]);
    return (_jsx(AriaErrorScope, { component: "div", form: props.form, fieldName: props.field.name, errorDisplay: props.errorDisplay, style: { width: "600px" }, "aria-label": typeof props.label === "string"
            ? props.label
            : props.options?.buttonText, children: _jsx(Grid, { item: true, xs: 12, children: _jsx(CheckBoxList, { items: items, loading: loading, onChange: newItem => updateItems(newItem), summaryComponent: Summary, markdownPostProcess: addTargetBlank }) }) }));
});
const mapAvailableAdditionalCoverages = (availableAdditionalCoverages, selectedCoverages, intl, descriptions) => {
    return availableAdditionalCoverages.map((additionalCoverage, idx) => {
        const selected = selectedCoverages.find(s => s.coverageTypeCode === additionalCoverage.coverageTypeCode);
        const checked = selected != null;
        const pricePerMonth = additionalCoverage?.deductibles[0].pricePerMonth;
        return {
            id: additionalCoverage.coverageTypeCode,
            primary: additionalCoverage.name ?? "",
            description: (descriptions &&
                descriptions?.find(d => d.value === additionalCoverage.coverageTypeCode)?.key) ??
                "",
            secondary: checked ? pricePerMonth.toString() + " kr/mnd" : undefined,
            checked,
            value: pricePerMonth,
            fields: [
                additionalCoverage.legalInsuranceAmounts.length > 0
                    ? {
                        fieldId: 1,
                        label: "Forsikringssum",
                        component: dropdownField,
                        options: additionalCoverage.legalInsuranceAmounts.map(lia => ({
                            label: lia.toString(),
                            value: lia
                        })),
                        value: selected?.insuranceAmount.toString() ??
                            additionalCoverage.insuranceAmount.toString()
                    }
                    : null
            ].filter(f => f !== null)
        };
    });
};
const dropdownField = ({ item, fieldId, value, onChange, options }) => (_jsx(DployAutocomplete, { variant: "standard", searchable: false, margin: "dense", items: options ?? [], getItemLabel: item => item?.label, value: options?.reduce((acc, o) => (o.value.toString() === value ? o : acc), undefined), onChange: (e, v) => {
        let newField = {
            ...item,
            checked: true,
            fields: item.fields?.map(field => field.fieldId === fieldId ? { ...field, value: v?.value } : field)
        };
        onChange(newField);
    }, disableClearable: true }));
const Summary = (items) => {
    const intl = useIntl();
    const totalPrice = items.reduce((acc, item) => item.checked && item.value && typeof item.value === "number"
        ? acc + item.value
        : acc, 0);
    return (_jsx(Grid, { container: true, children: _jsx(Grid, { item: true, container: true, xs: 12, justifyContent: "center", alignContent: "center", children: _jsx(ListItemText, { primary: "Sum", secondary: intl.formatNumber(totalPrice) + " kr/mnd" }) }) }));
};
const AdditionalCoveragesLiteral = (props) => {
    const { literalVariant, typographyVariant, suffix, ...rest } = props;
    const intl = useIntl();
    return props.field.value ? (_jsx(TextLiteral, { ...rest, field: {
            ...props.field,
            value: !isArray(props.field.value) ? [] : props.field.value
        }, multiple: true, items: props.field.value, getItemLabel: (item) => {
            const prettyNumber = intl.formatNumber(item.insuranceAmount);
            const selectedInsuranceAmount = (item.insuranceAmount ?? 0) > 0 ? ` - ${prettyNumber} kr` : "";
            return `${item.coverageName}${selectedInsuranceAmount}`;
        } })) : null;
};
const addTargetBlank = (html) => {
    const domParser = new DOMParser();
    const document = domParser.parseFromString(html, `text/html`);
    const serializer = new XMLSerializer();
    const links = document.querySelectorAll(`a`);
    links.forEach(link => {
        if (link.href) {
            link.target = `_blank`;
            link.rel = `noopener noreferrer`;
        }
    });
    return serializer.serializeToString(document);
};
AdditionalCoveragesField.displayName = "AdditionalCoveragesField";
AdditionalCoveragesLiteral.displayName = "AdditionalCoveragesLiteral";
export { AdditionalCoveragesField, AdditionalCoveragesLiteral };
//# sourceMappingURL=AdditionalCoveragesField.js.map